import React, { ReactNode } from 'react';

/**
 * Format definition for text markers
 */
interface FormatDefinition {
  /**
   * Regex pattern to match formatting syntax.
   * Currently we only use the first capture group for content, but patterns can have multiple.
   */
  pattern: RegExp;
  /** Function to render the extracted content with styling */
  render: (content: string, key: string) => ReactNode;
}

const FORMATTERS: FormatDefinition[] = [
  // Bold-italic pattern 1: **_text_**
  {
    pattern: /\*\*_(.*?)_\*\*/g,
    render: (content, key) => (
      <strong key={`bold-italic-${key}`}>
        <em>{content}</em>
      </strong>
    ),
  },
  // Bold-italic pattern 2: _**text**_
  {
    pattern: /_\*\*(.*?)\*\*_/g,
    render: (content, key) => (
      <strong key={`bold-italic-${key}`}>
        <em>{content}</em>
      </strong>
    ),
  },
  // Bold text (**text**)
  {
    pattern: /\*\*(.*?)\*\*/g,
    render: (content, key) => <strong key={`bold-${key}`}>{content}</strong>,
  },
  // Italic text (_text_)
  {
    pattern: /_(.*?)_/g,
    render: (content, key) => <em key={`italic-${key}`}>{content}</em>,
  },
];

/**
 * Process a text segment with a specific pattern and renderer
 *
 * @param text The text to process
 * @param pattern The regex pattern to match - can have multiple capture groups
 * @param renderer Function to create a React node from the match content
 * @returns Array of strings and React nodes
 */
function processTextSegment(
  text: string,
  pattern: RegExp,
  renderer: (content: string) => ReactNode,
): (string | ReactNode)[] {
  const result: (string | ReactNode)[] = [];
  let lastIndex = 0;

  const allMatches = Array.from(text.matchAll(pattern));

  for (const match of allMatches) {
    // Extract the content we want to format
    // match[0] = full match including markers (e.g., "**bold**")
    // match[1] = first capture group (e.g., "bold" from "**bold**")
    const fullMatch = match[0];
    const capturedContent = match[1] || '';
    const fullMatchLength = fullMatch.length;
    const matchStartPosition = match.index || 0;

    // Add text before the match
    if (matchStartPosition > lastIndex) {
      result.push(text.substring(lastIndex, matchStartPosition));
    }

    // Add the styled element with the captured content
    result.push(renderer(capturedContent));

    // Update last index to after this match
    lastIndex = matchStartPosition + fullMatchLength;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result;
}

/**
 * Apply multiple formatters to a string
 *
 * @param text The text to format
 * @returns Formatted text as React nodes
 */
function applyFormatters(text: string): ReactNode[] {
  let segments: (string | ReactNode)[] = [text];
  let counter = 0;

  // Apply each formatter in sequence
  FORMATTERS.forEach((formatter) => {
    segments = segments.flatMap((segment) => {
      // Skip already processed React elements
      if (typeof segment !== 'string') {
        return segment;
      }

      return processTextSegment(
        segment,
        formatter.pattern,
        content => formatter.render(content, (counter++).toString()),
      );
    });
  });

  return segments.length ? segments : [text];
}

/**
 * Safely formats message text with basic styling
 * Supports:
 * - Bold text with **text** syntax
 * - Italics with _text_ syntax
 * - Combined formatting (bold and italic) with **_text_** or _**text**_ syntax
 *
 * @param message The message to format
 * @returns Array of React nodes with formatting applied
 */
export function formatJsonText(message: string): ReactNode[] {
  if (!message) {
    return [];
  }

  return applyFormatters(message);
}
